import { isArray, isString } from "@huub-web/utils";

import type {
  UIActionType,
  UIAddModelChildAction,
  UIAPIRequestAction,
  UIDeleteModelChildAction,
  UIInputHighlightAction,
  UIInputUpdateAction,
  UIModelChildrenUpdateAction,
  UINavigateAction,
  UINotificationAction,
  UIPromptAction,
  UIRefreshTemplatesAction,
  UIScrollToAction,
} from "../types";
import { usePubSub } from "./usePubSub";
import { useDebug } from "./useDebug";
import { nextFrame } from "../utils";

export type ActionHandler = (action: UIActionType<string, unknown>) => void;

const MODEL_CHILDREN_UPDATE_DELAY_MS = 200;

/**
 * Handles actions from templates.
 */
export const useActions = () => {
  const pubSub = usePubSub();
  const { log } = useDebug();

  const handleAction: ActionHandler = (action) => {
    log("orange", "useActions", "handleAction", action);

    // Navigate action
    if (
      action?.type === "navigate" &&
      isArray((action as UINavigateAction)?.payload?.context)
    ) {
      nextFrame(() => {
        pubSub.publish("navigate", {
          context: (action as UINavigateAction)?.payload?.context,
          params: (action as UINavigateAction)?.payload?.params,
        });
      });
    }

    // Input update action
    else if (action?.type === "inputUpdate") {
      pubSub.publish("inputUpdate", (action as UIInputUpdateAction)?.payload);
    }

    // Model children update action
    else if (action?.type === "modelChildrenUpdate") {
      const payload = (action as UIModelChildrenUpdateAction)?.payload;
      const delayMs =
        (payload?.updateLevel ?? 0) * MODEL_CHILDREN_UPDATE_DELAY_MS;

      if (delayMs > 0) {
        setTimeout(() => {
          pubSub.publish("modelChildrenUpdate", payload);
        }, delayMs);
      } else {
        pubSub.publish("modelChildrenUpdate", payload);
      }
    }

    // Input highlight action
    else if (action?.type === "inputHighlight") {
      pubSub.publish("inputHighlight", {
        ids: (action as UIInputHighlightAction)?.payload?.ids,
      });
    }

    // Prompt action
    else if (action?.type === "prompt") {
      pubSub.publish("sendPrompt", {
        value: (action as UIPromptAction)?.payload,
      });
    }

    // Notification action
    else if (action?.type === "notification") {
      pubSub.publish("snackbar", {
        title: (action as UINotificationAction)?.payload?.title,
        body: (action as UINotificationAction)?.payload?.body,
        variant: (action as UINotificationAction)?.payload?.variant,
        autoHide: (action as UINotificationAction)?.payload?.autoHide,
        closeable: (action as UINotificationAction)?.payload?.closeable,
      });
    }

    // Refresh templates action
    else if (action?.type === "refreshTemplates") {
      nextFrame(() => {
        pubSub.publish("refreshTemplates", {
          id: (action as UIRefreshTemplatesAction)?.payload?.id,
        });
      });
    }

    // Open menu action
    else if (action?.type === "openMenu") {
      pubSub.publish("menu", { action: "open" });
    }

    // API action
    else if (action?.type === "apiRequest") {
      const apiPayload = (action as UIAPIRequestAction)?.payload;
      if (
        apiPayload &&
        isString(apiPayload.method) &&
        isString(apiPayload.endpoint)
      ) {
        pubSub.publish("apiRequest", {
          method: apiPayload.method,
          endpoint: apiPayload.endpoint,
          payload: apiPayload.payload,
        });
      } else {
        console.warn("Invalid API action payload", action);
      }
    }

    // Scroll to element action
    else if (action?.type === "scrollTo") {
      const elementId = (action as UIScrollToAction)?.payload?.elementId;
      if (elementId) {
        // Publish expandChild so collapsed panels can open before scrolling
        pubSub.publish("expandChild", { elementId });
        // Delay scroll to allow panel expansion animation
        setTimeout(() => {
          const el = document.getElementById(elementId);
          if (el) {
            el.scrollIntoView({ behavior: "smooth" });
          } else {
            console.warn(
              `[useActions] scrollTo: element "${elementId}" not found in DOM`,
            );
          }
        }, 150);
      } else {
        console.warn(
          "[useActions] scrollTo: missing elementId in payload",
          action,
        );
      }
    }

    // Add child to ModelChildrenTemplate
    else if (action?.type === "addModelChild") {
      const { targetId } = (action as UIAddModelChildAction).payload;
      if (targetId) {
        pubSub.publish("addModelChild", { targetId });
      }
    }

    // Delete child from ModelChildrenTemplate
    else if (action?.type === "deleteModelChild") {
      const { targetId, childId } = (action as UIDeleteModelChildAction)
        .payload;
      if (targetId && childId) {
        pubSub.publish("deleteModelChild", { targetId, childId });
      }
    }

    // Unknown action
    else {
      console.warn("Unknown or incorrect action", action);
    }
  };

  return { handleAction };
};
